import { cn } from "@/shared/lib/cn";

type TablePaginationLabels = {
  showing: string;
  to: string;
  of: string;
  entries: string;
  previous: string;
  next: string;
};

type TablePaginationProps = {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  labels: TablePaginationLabels;
  className?: string;
};

function buildPageItems(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: (number | "ellipsis")[] = [1];

  if (currentPage > 3) {
    items.push("ellipsis");
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (currentPage < totalPages - 2) {
    items.push("ellipsis");
  }

  items.push(totalPages);
  return items;
}

export function TablePagination({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  labels,
  className,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const start = totalItems === 0 ? 0 : (safePage - 1) * pageSize;
  const pageItems = buildPageItems(safePage, totalPages);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted",
        className,
      )}
    >
      <span>
        {labels.showing} {totalItems === 0 ? 0 : start + 1} {labels.to}{" "}
        {Math.min(start + pageSize, totalItems)} {labels.of} {totalItems}{" "}
        {labels.entries}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={safePage <= 1}
          onClick={() => onPageChange(safePage - 1)}
          className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
        >
          {labels.previous}
        </button>
        {pageItems.map((item, index) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              className={cn(
                "min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                item === safePage
                  ? "bg-primary text-white"
                  : "border border-border text-muted-foreground hover:bg-card-muted",
              )}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          disabled={safePage >= totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
        >
          {labels.next}
        </button>
      </div>
    </div>
  );
}
